import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2 } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { CreateAutomationDialog } from "@/routes/automations/CreateAutomationDialog";

interface Automation {
  _id: string;
  name: string;
  steps: Array<{
    type: string;
    data: any;
  }>;
  createdAt: string;
  updatedAt: string;
  template?: { type: string; context?: unknown } | string | boolean;
}

export function Automations() {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/automations");

        if (response.ok) {
          const data = await response.json();
          const filteredAutomations = (data.automations || []).filter(
            (automation: Automation) => {
              const template = automation.template;
              return template !== true && typeof template !== "string";
            }
          );
          setAutomations(filteredAutomations);
        } else {
          console.error("Failed to fetch automations");
        }

        setBreadcrumbs([
          { label: "Flows", path: "/automations" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this flow?")) return;
    setDeletingId(id);
    try {
      const response = await fetch(`/api/automations/${id}`, { method: "DELETE" });
      if (response.ok) {
        setAutomations((prev) => prev.filter((a) => a._id !== id));
      } else {
        const data = await response.json();
        alert(data.error || "Failed to delete flow");
      }
    } catch (error) {
      console.error("Failed to delete automation:", error);
      alert("Failed to delete flow");
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button
          data-testid="automations-create"
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Create
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Steps</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {automations && automations.length > 0 ? (
            automations.map((automation) => (
              <TableRow 
                key={automation._id}
                className="cursor-pointer hover:bg-muted/50"
                data-testid="automations-row"
                data-name={automation.name}
                onClick={() => {
                  navigate(`/automations/${automation._id}/edit`);
                }}
              >
                <TableCell>{automation.name}</TableCell>
                <TableCell>{automation.steps.length}</TableCell>
                <TableCell>{formatDate(automation.createdAt)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={(e) => handleDelete(e, automation._id)}
                    disabled={deletingId === automation._id}
                  >
                    {deletingId === automation._id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="h-24 text-center">
                No flows found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <CreateAutomationDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />
    </>
  );
}
